import {
  TB_DESCRIPTION_MAX_LENGTH,
  TB_REF_MAX_LENGTH,
  TrialBalanceType,
  type PeriodOption,
  type TrialBalanceEditModel,
} from '@/core/models/trial-balance.models';
import { NO_PERIOD_PLACEHOLDER } from '@/core/format/periodLabel';
import { notifyError } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { DateField } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { Radio, RadioGroup } from '@/kit/Radio';
import { Select } from '@/kit/Select';
import type { EditModel } from './trialBalanceEditor';

interface TrialBalanceDetailsProps {
  /** The shared model; changed only through `onEdit` (the editor applies the change and refreshes every panel). */
  model: TrialBalanceEditModel;
  periods: PeriodOption[];
  onEdit: EditModel;
}

/**
 * Angular TrialBalanceDetailsComponent: the "Edit Details" tab (header fields). BUG-038 (item 123): the CSV template
 * and file picker are part of Create Trial Balance only - an import happens when the trial balance is created - so
 * this tab shows the Mode of Import read-only.
 */
export function TrialBalanceDetails({ model, periods, onEdit }: TrialBalanceDetailsProps) {
  /** ap5-a6 (item 115): annual = period-driven dates, management only = user entered dates. */
  const isAnnual = model?.typeId === TrialBalanceType.Annual;
  /** #195: once saved the mode is fixed. BUG-038: a new one has the mode chosen (and imported) in Create Trial Balance. */
  const isSaved = Boolean(model?.id);
  /** Issue #36 (QA): once a report has been created from a trial balance (even one deleted since) its type is fixed. */
  const typeLocked = Boolean(model?.isUsedByReport);

  /** Switching to management-only drops the accounting period the annual dates came from. */
  const onTypeChange = (typeId: number) =>
    onEdit((m) => {
      m.typeId = typeId;
      if (m.typeId !== TrialBalanceType.Annual) {
        m.accountingPeriodId = null;
        m.periodLabel = '';
      }
    });

  const onPeriodChange = (period: PeriodOption | null | undefined) =>
    onEdit((m) => {
      m.accountingPeriodId = period?.id ?? null;
      m.periodLabel = period?.label ?? '';
      if (period) {
        m.fromDate = period.periodFrom;
        m.toDate = period.periodTo;
      }
    });

  return (
    <div className="sidenav-body-content p-0">
      <div className="wp-100">
        <div>
          <div className="form-field">
            <p>Ref. No.</p>
            <InputField
              fieldClassName="w-387"
              charMode="alphanumeric"
              name="refNo"
              maxLength={TB_REF_MAX_LENGTH}
              value={model.refNo}
              onChange={(event) => {
                const value = event.target.value;
                onEdit((m) => {
                  m.refNo = value;
                });
              }}
            />
          </div>
          {/* BUG-037: one line of at most 50 characters, as the old application (shared with other Capium modules). A
              longer value saved before still shows and saves unchanged; only a new one is held to the limit. */}
          <div className="form-field">
            <p>Description</p>
            <InputField
              fieldClassName="w-387"
              name="description"
              maxLength={TB_DESCRIPTION_MAX_LENGTH}
              value={model.description}
              onChange={(event) => {
                const value = event.target.value;
                onEdit((m) => {
                  m.description = value;
                });
              }}
            />
          </div>
        </div>
        <div className="content-box hr-top">
          <div className="form-field toggle-option mt-1 mb-0">
            <p className="pb-5">Type</p>
            <RadioGroup value={model.typeId} disabled={typeLocked} onChange={(value) => onTypeChange(value as number)}>
              <div className="d-flex align-center gap-15">
                <Radio value={0} hostClassName="example-radio-button">
                  Annual
                </Radio>
                <Radio value={1} hostClassName="example-radio-button">
                  Management only
                </Radio>
              </div>
            </RadioGroup>
            {typeLocked && (
              <p className="font-size-12 text-gray mb-0">
                A report has been created from this trial balance, so its type cannot be changed.
              </p>
            )}
          </div>
          {isAnnual && (
            <div className="form-field">
              <p>Accounting Period</p>
              <Select<PeriodOption>
                className="w-387"
                placeholder={NO_PERIOD_PLACEHOLDER}
                items={periods}
                bindLabel="label"
                bindValue="id"
                value={model.accountingPeriodId || null}
                onChange={(_, item) => onPeriodChange(item)}
              />
            </div>
          )}
          <div className="d-flex gap-30">
            <div className="form-field">
              <p>From</p>
              <DateField
                fieldClassName="w-180"
                format="DD-MMM-YYYY"
                readOnly
                disabled={isAnnual}
                value={model.fromDate}
                onChange={(value) => {
                  const problem = dateRangeProblem(value, model.toDate, 'The period');
                  if (problem) {
                    notifyError(problem);
                    return;
                  }
                  onEdit((m) => {
                    m.fromDate = value;
                  });
                }}
              />
            </div>
            <div className="form-field">
              <p>To</p>
              <DateField
                fieldClassName="w-180"
                format="DD-MMM-YYYY"
                readOnly
                disabled={isAnnual}
                value={model.toDate}
                onChange={(value) => {
                  const problem = dateRangeProblem(model.fromDate, value, 'The period');
                  if (problem) {
                    notifyError(problem);
                    return;
                  }
                  onEdit((m) => {
                    m.toDate = value;
                  });
                }}
              />
            </div>
          </div>
          {isAnnual && (
            <div className="text-gray font-size-12">
              The reporting period follows the accounting period selected above.
            </div>
          )}
        </div>
        <div className="content-box hr-top">
          <div className="form-field toggle-option mt-1 mb-0">
            <p className="pb-5">Mode of Import</p>
            <RadioGroup value={model.importModeId} disabled onChange={() => undefined}>
              <div className="d-flex align-center gap-15">
                <Radio value={0} hostClassName="example-radio-button">
                  Bookkeeping
                </Radio>
                <Radio value={1} hostClassName="example-radio-button">
                  CSV
                </Radio>
                <Radio value={2} hostClassName="example-radio-button">
                  Manual
                </Radio>
                <Radio value={3} hostClassName="example-radio-button">
                  QuickBooks
                </Radio>
                <Radio value={4} hostClassName="example-radio-button">
                  Xero
                </Radio>
                <Radio value={5} hostClassName="example-radio-button">
                  FreeAgent
                </Radio>
              </div>
            </RadioGroup>
            <div className="text-gray font-size-12">
              {isSaved
                ? 'The mode of import is fixed once the trial balance is saved.'
                : 'The mode of import is chosen in Create Trial Balance.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
